import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import TrustBadge from '@/components/common/TrustBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicEmbedWidget = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEmbed = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/embed/public/${token}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Widget not found');
        }
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchEmbed();
  }, [token]);

  // iter52 — fire click beacon before the browser follows the anchor href.
  // navigator.sendBeacon is queued by the browser and delivered even if the page
  // unloads immediately. Falls back to fetch(keepalive) for older browsers.
  const trackClick = (contentId) => {
    if (!contentId) return;
    const url = `${BACKEND_URL}/api/embed/public/${token}/click`;
    const payload = JSON.stringify({ content_id: contentId });
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      /* analytics is best-effort — never block navigation */
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: '#0A1024' }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: '#DC2626' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen p-4" style={{ background: '#0A1024' }}>
        <div className="text-center">
          <AlertCircle className="h-8 w-8 mx-auto mb-2" style={{ color: '#F59E0B' }} />
          <p style={{ color: '#94A3B8', fontSize: '14px' }}>{error}</p>
        </div>
      </div>
    );
  }

  const isDark = data.theme === 'dark';
  const bg = isDark ? '#0F1730' : '#FFFFFF';
  const cardBg = isDark ? '#243554' : '#F9FAFB';
  const textPrimary = isDark ? '#F3F4F6' : '#0F1730';
  const textSecondary = isDark ? '#9CA3AF' : '#6B7280';
  const border = isDark ? '#374151' : '#E5E7EB';
  const accent = '#DC2626';
  const tagBg = isDark ? '#374151' : '#F3F4F6';
  const tagText = isDark ? '#D1D5DB' : '#4B5563';

  return (
    <div
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: bg,
        minHeight: '100vh',
        padding: '16px',
      }}
      data-testid="embed-widget-container"
    >
      {/* Widget Header */}
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 700, color: textPrimary, margin: 0 }} data-testid="embed-widget-title">
          {data.name}
        </h2>
        <span style={{ fontSize: '11px', color: textSecondary, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {/* iter178 — inline "Verified" mark next to "powered by dotbox" */}
          <TrustBadge trust={data.trust} variant="inline" />
          powered by <a href="/" target="_blank" rel="noopener noreferrer" style={{ color: accent, textDecoration: 'none', fontWeight: 600 }}>dotbox</a>
        </span>
      </div>

      {/* Content Items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {data.items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: textSecondary, fontSize: '14px' }}>
            No content available
          </div>
        ) : (
          data.items.map((item, i) => (
            <a
              key={item.id || i}
              href={item.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick(item.id)}
              style={{
                display: 'block',
                background: cardBg,
                border: `1px solid ${border}`,
                borderRadius: '8px',
                padding: '14px',
                textDecoration: 'none',
                transition: 'border-color 0.15s',
              }}
              data-testid="embed-content-item"
              onMouseEnter={e => { e.currentTarget.style.borderColor = accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = border; }}
            >
              {/* Title */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, color: textPrimary, margin: 0, flex: 1, lineHeight: '1.4' }}>
                  {item.title}
                </h3>
                <ExternalLink style={{ width: '14px', height: '14px', color: textSecondary, flexShrink: 0, marginTop: '2px' }} />
              </div>

              {/* Summary */}
              {data.show_summary && item.summary && (
                <p style={{ fontSize: '12px', color: textSecondary, margin: '6px 0 0', lineHeight: '1.5', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {item.summary}
                </p>
              )}

              {/* Meta Row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                {data.show_source && item.source_name && (
                  <span style={{ fontSize: '11px', color: accent, fontWeight: 500 }}>
                    {item.source_name}
                  </span>
                )}
                {item.published_at && (
                  <span style={{ fontSize: '11px', color: textSecondary }}>
                    {new Date(item.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                )}
                {data.show_tags && item.tags && item.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {item.tags.slice(0, 3).map((tag, ti) => (
                      <span key={ti} style={{ fontSize: '10px', background: tagBg, color: tagText, padding: '1px 6px', borderRadius: '4px' }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </a>
          ))
        )}
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: '14px', paddingTop: '10px', borderTop: `1px solid ${border}` }}>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: '11px', color: textSecondary, textDecoration: 'none' }}
        >
          Content curated by <span style={{ color: accent, fontWeight: 600 }}>dotbox</span>
        </a>
      </div>
    </div>
  );
};

export default PublicEmbedWidget;
